/**
 * Shared country data for pickers across native and web.
 * Source of truth: country-state-city country list plus curated startup-safe
 * city/location data.
 *
 * Phone codes: ALL countries from country-state-city.
 * Nationalities: Comprehensive list grouped by region.
 * Locations: curated top cities. Do not import the package city database here:
 *            it adds roughly 8 MB to the web startup bundle.
 */
import Country from 'country-state-city/lib/country';

// ─── Types ──────────────────────────────────────────────────────────

export interface CountryCode {
  name: string;
  dial: string;
  flag: string;
  code: string;
}

export interface NationalityGroup {
  label: string;
  items: string[];
}

export interface LocationSection {
  title: string;
  data: string[];
}

// ─── Phone Codes (ALL countries) ────────────────────────────────────
// GCC + key markets shown first, then rest alphabetically.

const PRIORITY_ORDER = [
  'AE', 'SA', 'QA', 'KW', 'BH', 'OM',
  'JO', 'LB', 'EG', 'IQ', 'SY', 'PS', 'YE', 'TN', 'MA', 'DZ', 'SD', 'LY', 'IR',
  'IN', 'PK', 'BD', 'LK', 'NP', 'AF',
  'PH', 'ID', 'MY', 'SG', 'TH', 'VN', 'MM',
  'CN', 'JP', 'KR', 'TW', 'HK',
  'GB', 'FR', 'DE', 'IT', 'ES', 'NL', 'BE', 'CH', 'SE', 'NO', 'DK', 'FI', 'IE', 'PT', 'GR', 'PL', 'RO', 'CZ', 'HU', 'AT', 'UA', 'RU',
  'US', 'CA', 'MX', 'BR', 'AR', 'CO', 'CL', 'PE',
  'ZA', 'NG', 'KE', 'ET', 'GH', 'TZ', 'UG', 'RW', 'ZW',
  'AU', 'NZ',
  'TR',
];

function buildPhoneCodes(): CountryCode[] {
  const all = Country.getAllCountries();
  const result: CountryCode[] = [];
  const seen = new Set<string>();

  for (const code of PRIORITY_ORDER) {
    const c = all.find((x) => x.isoCode === code);
    if (c && c.phonecode) {
      const dial = c.phonecode.startsWith('+') ? c.phonecode : `+${c.phonecode}`;
      // Skip codes with dashes (compound codes like +358-18)
      if (!dial.includes('-')) {
        result.push({ name: c.name, dial, flag: c.flag, code: c.isoCode });
        seen.add(c.isoCode);
      }
    }
  }

  // All remaining countries alphabetically
  for (const c of all) {
    if (seen.has(c.isoCode)) continue;
    if (!c.phonecode) continue;
    const dial = c.phonecode.startsWith('+') ? c.phonecode : `+${c.phonecode}`;
    if (dial.includes('-')) continue;
    result.push({ name: c.name, dial, flag: c.flag, code: c.isoCode });
  }

  return result;
}

export const PHONE_CODES: CountryCode[] = buildPhoneCodes();
export const DEFAULT_COUNTRY: CountryCode = PHONE_CODES[0]; // UAE

// ─── Nationalities (comprehensive, grouped by region) ───────────────

export const NATIONALITY_GROUPS: NationalityGroup[] = [
  {
    label: 'GCC',
    items: ['Emirati', 'Saudi', 'Qatari', 'Kuwaiti', 'Bahraini', 'Omani'],
  },
  {
    label: 'Middle East & North Africa',
    items: ['Jordanian', 'Lebanese', 'Egyptian', 'Iraqi', 'Syrian', 'Palestinian', 'Yemeni', 'Tunisian', 'Moroccan', 'Algerian', 'Sudanese', 'Libyan', 'Iranian', 'Turkish'],
  },
  {
    label: 'South Asia',
    items: ['Indian', 'Pakistani', 'Bangladeshi', 'Sri Lankan', 'Nepali', 'Afghan', 'Maldivian', 'Bhutanese'],
  },
  {
    label: 'Southeast Asia',
    items: ['Filipino', 'Indonesian', 'Malaysian', 'Singaporean', 'Thai', 'Vietnamese', 'Myanmar', 'Cambodian', 'Laotian'],
  },
  {
    label: 'East Asia',
    items: ['Chinese', 'Japanese', 'South Korean', 'Taiwanese', 'Hong Konger', 'Mongolian'],
  },
  {
    label: 'Europe',
    items: ['British', 'French', 'German', 'Italian', 'Spanish', 'Dutch', 'Belgian', 'Swiss', 'Swedish', 'Norwegian', 'Danish', 'Finnish', 'Irish', 'Portuguese', 'Greek', 'Polish', 'Romanian', 'Czech', 'Hungarian', 'Austrian', 'Ukrainian', 'Russian', 'Serbian', 'Croatian', 'Bulgarian', 'Slovak', 'Slovenian', 'Lithuanian', 'Latvian', 'Estonian', 'Icelandic', 'Luxembourgish', 'Maltese', 'Cypriot', 'Bosnian', 'Albanian', 'Macedonian', 'Montenegrin', 'Kosovar', 'Moldovan', 'Belarusian', 'Georgian', 'Armenian', 'Azerbaijani'],
  },
  {
    label: 'Americas',
    items: ['American', 'Canadian', 'Mexican', 'Brazilian', 'Argentine', 'Colombian', 'Chilean', 'Peruvian', 'Venezuelan', 'Ecuadorian', 'Bolivian', 'Paraguayan', 'Uruguayan', 'Costa Rican', 'Panamanian', 'Cuban', 'Dominican', 'Puerto Rican', 'Guatemalan', 'Honduran', 'Salvadoran', 'Jamaican', 'Trinidadian', 'Haitian'],
  },
  {
    label: 'Sub-Saharan Africa',
    items: ['South African', 'Nigerian', 'Kenyan', 'Ethiopian', 'Ghanaian', 'Tanzanian', 'Ugandan', 'Rwandan', 'Zimbabwean', 'Congolese', 'Cameroonian', 'Ivorian', 'Senegalese', 'Malian', 'Burkinabe', 'Somali', 'Eritrean', 'Mozambican', 'Zambian', 'Malawian', 'Botswanan', 'Namibian', 'Angolan', 'Mauritian', 'Seychellois', 'Malagasy'],
  },
  {
    label: 'Central Asia',
    items: ['Kazakh', 'Uzbek', 'Turkmen', 'Kyrgyz', 'Tajik'],
  },
  {
    label: 'Oceania',
    items: ['Australian', 'New Zealander', 'Fijian', 'Samoan', 'Tongan', 'Papua New Guinean'],
  },
  {
    label: 'Caribbean',
    items: ['Bahamian', 'Barbadian', 'Bermudian', 'Caymanian'],
  },
  {
    label: 'Other',
    items: ['Other'],
  },
];

// ─── Locations ──────────────────────────────────────────────────────
// Curated top cities for the initial picker view (fast, no large bundle).

const REGION_CODES: Record<string, string[]> = {
  GCC: ['AE', 'SA', 'QA', 'KW', 'BH', 'OM'],
  'Middle East': ['JO', 'LB', 'EG', 'IQ'],
  'South Asia': ['IN', 'PK', 'BD'],
  'Southeast Asia': ['PH', 'MY', 'SG', 'TH'],
  'East Asia': ['CN', 'JP', 'KR'],
  Europe: ['GB', 'DE', 'FR', 'NL', 'IT', 'ES'],
  Americas: ['US', 'CA', 'BR', 'MX'],
  Africa: ['ZA', 'NG', 'KE'],
  Oceania: ['AU', 'NZ'],
};

export const TOP_CITIES: Record<string, string[]> = {
  AE: ['Abu Dhabi', 'Dubai', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Al Ain'],
  SA: ['Riyadh', 'Jeddah', 'Dammam', 'Makkah', 'Madinah', 'Khobar'],
  QA: ['Doha', 'Al Wakrah', 'Al Rayyan'],
  KW: ['Kuwait City', 'Hawalli', 'Salmiya'],
  BH: ['Manama', 'Muharraq', 'Riffa'],
  OM: ['Muscat', 'Salalah', 'Sohar'],
  JO: ['Amman', 'Irbid', 'Zarqa'],
  LB: ['Beirut', 'Tripoli', 'Sidon'],
  EG: ['Cairo', 'Alexandria', 'Giza', 'Sharm El Sheikh'],
  IQ: ['Baghdad', 'Erbil', 'Basra', 'Sulaymaniyah'],
  IN: ['Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai', 'Pune', 'Kolkata', 'Ahmedabad'],
  PK: ['Karachi', 'Lahore', 'Islamabad', 'Rawalpindi', 'Faisalabad', 'Peshawar'],
  PH: ['Manila', 'Cebu City', 'Davao', 'Quezon City'],
  BD: ['Dhaka', 'Chittagong', 'Sylhet', 'Khulna'],
  MY: ['Kuala Lumpur', 'Penang', 'Johor Bahru', 'Kota Kinabalu'],
  SG: ['Singapore'],
  TH: ['Bangkok', 'Chiang Mai', 'Phuket'],
  CN: ['Beijing', 'Shanghai', 'Shenzhen', 'Guangzhou', 'Hangzhou'],
  JP: ['Tokyo', 'Osaka', 'Yokohama', 'Nagoya', 'Kyoto'],
  KR: ['Seoul', 'Busan', 'Incheon'],
  GB: ['London', 'Manchester', 'Birmingham', 'Edinburgh', 'Leeds', 'Glasgow'],
  DE: ['Berlin', 'Munich', 'Frankfurt', 'Hamburg', 'Cologne'],
  FR: ['Paris', 'Lyon', 'Marseille', 'Toulouse', 'Nice'],
  NL: ['Amsterdam', 'Rotterdam', 'The Hague', 'Utrecht'],
  IT: ['Rome', 'Milan', 'Naples', 'Turin', 'Florence'],
  ES: ['Madrid', 'Barcelona', 'Valencia', 'Seville'],
  US: ['New York', 'Los Angeles', 'Chicago', 'Houston', 'San Francisco', 'Seattle', 'Miami', 'Boston', 'Washington DC', 'Austin'],
  CA: ['Toronto', 'Vancouver', 'Montreal', 'Calgary', 'Ottawa'],
  BR: ['Sao Paulo', 'Rio de Janeiro', 'Brasilia'],
  MX: ['Mexico City', 'Guadalajara', 'Monterrey'],
  ZA: ['Johannesburg', 'Cape Town', 'Durban', 'Pretoria'],
  NG: ['Lagos', 'Abuja', 'Port Harcourt'],
  KE: ['Nairobi', 'Mombasa'],
  AU: ['Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide'],
  NZ: ['Auckland', 'Wellington', 'Christchurch'],
};

export interface LocationMeta {
  city: string;
  countryCode: string;
  countryName: string;
}

/**
 * Canonical `${city}, ${country}` value -> its parts, so a renderer can
 * localize the label without the stored/queried value ever changing.
 * Covers every curated city, not just the ones surfaced by REGION_CODES,
 * so `getCitiesForCountry` output resolves too.
 */
export const LOCATION_META: Record<string, LocationMeta> = (() => {
  const meta: Record<string, LocationMeta> = {};
  for (const [countryCode, cities] of Object.entries(TOP_CITIES)) {
    const country = Country.getCountryByCode(countryCode);
    if (!country) continue;
    for (const city of cities) {
      meta[`${city}, ${country.name}`] = {
        city,
        countryCode,
        countryName: country.name,
      };
    }
  }
  return meta;
})();

export function buildLocationSections(): LocationSection[] {
  const result: LocationSection[] = [{ title: 'Remote', data: ['Remote'] }];

  for (const [region, codes] of Object.entries(REGION_CODES)) {
    const locations: string[] = [];
    for (const code of codes) {
      const country = Country.getCountryByCode(code);
      if (!country) continue;
      for (const city of (TOP_CITIES[code] || [])) {
        locations.push(`${city}, ${country.name}`);
      }
    }
    if (locations.length > 0) result.push({ title: region, data: locations });
  }

  return result;
}

export const LOCATION_SECTIONS: LocationSection[] = buildLocationSections();
export const ALL_LOCATIONS: string[] = LOCATION_SECTIONS.flatMap((s) => s.data);

// Maps a curated location-section title to its i18n key under `common.location.regions`,
// so the section headers (Remote / GCC / regions) can be localized at render time
// while the underlying data + stored values stay English-canonical.
export const REGION_I18N_KEY: Record<string, string> = {
  Remote: 'remote',
  GCC: 'gcc',
  'Middle East': 'middleEast',
  'South Asia': 'southAsia',
  'Southeast Asia': 'southeastAsia',
  'East Asia': 'eastAsia',
  Europe: 'europe',
  Americas: 'americas',
  Africa: 'africa',
  Oceania: 'oceania',
};

export function getCitiesForCountry(countryCode: string): string[] {
  const country = Country.getCountryByCode(countryCode);
  if (!country) return [];
  return (TOP_CITIES[countryCode] || []).map((city) => `${city}, ${country.name}`);
}

// ─── Nationality → ISO country code ─────────────────────────────────
// Maps demonyms (from NATIONALITY_GROUPS) and English country names
// (from UAE Pass `nationalityEN`) to ISO 3166-1 alpha-2 codes.

const NATIONALITY_TO_ISO: Record<string, string> = {
  // GCC
  emirati: 'AE', 'united arab emirates': 'AE', uae: 'AE',
  saudi: 'SA', 'saudi arabian': 'SA', 'saudi arabia': 'SA',
  qatari: 'QA', qatar: 'QA',
  kuwaiti: 'KW', kuwait: 'KW',
  bahraini: 'BH', bahrain: 'BH',
  omani: 'OM', oman: 'OM',
  // MENA
  jordanian: 'JO', jordan: 'JO',
  lebanese: 'LB', lebanon: 'LB',
  egyptian: 'EG', egypt: 'EG',
  iraqi: 'IQ', iraq: 'IQ',
  syrian: 'SY', syria: 'SY', 'syrian arab republic': 'SY',
  palestinian: 'PS', palestine: 'PS', 'state of palestine': 'PS',
  yemeni: 'YE', yemen: 'YE',
  tunisian: 'TN', tunisia: 'TN',
  moroccan: 'MA', morocco: 'MA',
  algerian: 'DZ', algeria: 'DZ',
  sudanese: 'SD', sudan: 'SD',
  libyan: 'LY', libya: 'LY',
  iranian: 'IR', iran: 'IR', 'islamic republic of iran': 'IR',
  turkish: 'TR', turkey: 'TR', türkiye: 'TR', turkiye: 'TR',
  // South Asia
  indian: 'IN', india: 'IN',
  pakistani: 'PK', pakistan: 'PK',
  bangladeshi: 'BD', bangladesh: 'BD',
  'sri lankan': 'LK', 'sri lanka': 'LK',
  nepali: 'NP', nepalese: 'NP', nepal: 'NP',
  afghan: 'AF', afghani: 'AF', afghanistan: 'AF',
  maldivian: 'MV', maldives: 'MV',
  bhutanese: 'BT', bhutan: 'BT',
  // SE Asia
  filipino: 'PH', philippines: 'PH', 'the philippines': 'PH',
  indonesian: 'ID', indonesia: 'ID',
  malaysian: 'MY', malaysia: 'MY',
  singaporean: 'SG', singapore: 'SG',
  thai: 'TH', thailand: 'TH',
  vietnamese: 'VN', vietnam: 'VN', 'viet nam': 'VN',
  myanmar: 'MM', burmese: 'MM',
  cambodian: 'KH', cambodia: 'KH', khmer: 'KH',
  laotian: 'LA', lao: 'LA', laos: 'LA',
  // East Asia
  chinese: 'CN', china: 'CN',
  japanese: 'JP', japan: 'JP',
  'south korean': 'KR', korean: 'KR', 'korea, republic of': 'KR', 'south korea': 'KR',
  taiwanese: 'TW', taiwan: 'TW',
  'hong konger': 'HK', 'hong kong': 'HK',
  mongolian: 'MN', mongolia: 'MN',
  // Europe
  british: 'GB', 'united kingdom': 'GB', uk: 'GB', 'great britain': 'GB',
  french: 'FR', france: 'FR',
  german: 'DE', germany: 'DE',
  italian: 'IT', italy: 'IT',
  spanish: 'ES', spain: 'ES',
  dutch: 'NL', netherlands: 'NL', 'the netherlands': 'NL',
  belgian: 'BE', belgium: 'BE',
  swiss: 'CH', switzerland: 'CH',
  swedish: 'SE', sweden: 'SE',
  norwegian: 'NO', norway: 'NO',
  danish: 'DK', denmark: 'DK',
  finnish: 'FI', finland: 'FI',
  irish: 'IE', ireland: 'IE',
  portuguese: 'PT', portugal: 'PT',
  greek: 'GR', greece: 'GR',
  polish: 'PL', poland: 'PL',
  romanian: 'RO', romania: 'RO',
  czech: 'CZ', czechia: 'CZ', 'czech republic': 'CZ',
  hungarian: 'HU', hungary: 'HU',
  austrian: 'AT', austria: 'AT',
  ukrainian: 'UA', ukraine: 'UA',
  russian: 'RU', russia: 'RU', 'russian federation': 'RU',
  serbian: 'RS', serbia: 'RS',
  croatian: 'HR', croatia: 'HR',
  bulgarian: 'BG', bulgaria: 'BG',
  slovak: 'SK', slovakia: 'SK',
  slovenian: 'SI', slovenia: 'SI',
  lithuanian: 'LT', lithuania: 'LT',
  latvian: 'LV', latvia: 'LV',
  estonian: 'EE', estonia: 'EE',
  icelandic: 'IS', iceland: 'IS',
  luxembourgish: 'LU', luxembourg: 'LU',
  maltese: 'MT', malta: 'MT',
  cypriot: 'CY', cyprus: 'CY',
  bosnian: 'BA', 'bosnia and herzegovina': 'BA', bosnia: 'BA',
  albanian: 'AL', albania: 'AL',
  macedonian: 'MK', 'north macedonia': 'MK',
  montenegrin: 'ME', montenegro: 'ME',
  kosovar: 'XK', kosovo: 'XK',
  moldovan: 'MD', moldova: 'MD',
  belarusian: 'BY', belarus: 'BY',
  georgian: 'GE', georgia: 'GE',
  armenian: 'AM', armenia: 'AM',
  azerbaijani: 'AZ', azerbaijan: 'AZ',
  // Americas
  american: 'US', 'united states': 'US', 'united states of america': 'US', usa: 'US',
  canadian: 'CA', canada: 'CA',
  mexican: 'MX', mexico: 'MX',
  brazilian: 'BR', brazil: 'BR',
  argentine: 'AR', argentinian: 'AR', argentina: 'AR',
  colombian: 'CO', colombia: 'CO',
  chilean: 'CL', chile: 'CL',
  peruvian: 'PE', peru: 'PE',
  venezuelan: 'VE', venezuela: 'VE',
  ecuadorian: 'EC', ecuador: 'EC',
  bolivian: 'BO', bolivia: 'BO',
  paraguayan: 'PY', paraguay: 'PY',
  uruguayan: 'UY', uruguay: 'UY',
  'costa rican': 'CR', 'costa rica': 'CR',
  panamanian: 'PA', panama: 'PA',
  cuban: 'CU', cuba: 'CU',
  dominican: 'DO', 'dominican republic': 'DO',
  'puerto rican': 'PR', 'puerto rico': 'PR',
  guatemalan: 'GT', guatemala: 'GT',
  honduran: 'HN', honduras: 'HN',
  salvadoran: 'SV', 'el salvador': 'SV',
  jamaican: 'JM', jamaica: 'JM',
  trinidadian: 'TT', 'trinidad and tobago': 'TT',
  haitian: 'HT', haiti: 'HT',
  // Sub-Saharan Africa
  'south african': 'ZA', 'south africa': 'ZA',
  nigerian: 'NG', nigeria: 'NG',
  kenyan: 'KE', kenya: 'KE',
  ethiopian: 'ET', ethiopia: 'ET',
  ghanaian: 'GH', ghana: 'GH',
  tanzanian: 'TZ', tanzania: 'TZ',
  ugandan: 'UG', uganda: 'UG',
  rwandan: 'RW', rwanda: 'RW',
  zimbabwean: 'ZW', zimbabwe: 'ZW',
  congolese: 'CD', 'democratic republic of the congo': 'CD',
  cameroonian: 'CM', cameroon: 'CM',
  ivorian: 'CI', "côte d'ivoire": 'CI', 'ivory coast': 'CI',
  senegalese: 'SN', senegal: 'SN',
  malian: 'ML', mali: 'ML',
  burkinabe: 'BF', 'burkina faso': 'BF',
  somali: 'SO', somalia: 'SO',
  eritrean: 'ER', eritrea: 'ER',
  mozambican: 'MZ', mozambique: 'MZ',
  zambian: 'ZM', zambia: 'ZM',
  malawian: 'MW', malawi: 'MW',
  botswanan: 'BW', motswana: 'BW', botswana: 'BW',
  namibian: 'NA', namibia: 'NA',
  angolan: 'AO', angola: 'AO',
  mauritian: 'MU', mauritius: 'MU',
  seychellois: 'SC', seychelles: 'SC',
  malagasy: 'MG', madagascar: 'MG',
  // Central Asia
  kazakh: 'KZ', kazakhstan: 'KZ',
  uzbek: 'UZ', uzbekistan: 'UZ',
  turkmen: 'TM', turkmenistan: 'TM',
  kyrgyz: 'KG', kyrgyzstan: 'KG',
  tajik: 'TJ', tajikistan: 'TJ',
  // Oceania
  australian: 'AU', australia: 'AU',
  'new zealander': 'NZ', kiwi: 'NZ', 'new zealand': 'NZ',
  fijian: 'FJ', fiji: 'FJ',
  samoan: 'WS', samoa: 'WS',
  tongan: 'TO', tonga: 'TO',
  'papua new guinean': 'PG', 'papua new guinea': 'PG',
  // Caribbean
  bahamian: 'BS', bahamas: 'BS', 'the bahamas': 'BS',
  barbadian: 'BB', barbados: 'BB',
  bermudian: 'BM', bermuda: 'BM',
  caymanian: 'KY', 'cayman islands': 'KY',
};

/**
 * Resolve a nationality string (demonym or English country name) to an
 * ISO 3166-1 alpha-2 country code. Returns null if no match.
 */
export function nationalityToCountryCode(nationality: string | null | undefined): string | null {
  if (!nationality) return null;
  const key = nationality.trim().toLowerCase();
  if (!key) return null;
  return NATIONALITY_TO_ISO[key] ?? null;
}

/**
 * Search the startup-safe curated city list. Full city search should move to a
 * backend endpoint so the 148K city dataset is not bundled into Expo web.
 */
export function searchCities(query: string, limit = 20): string[] {
  if (!query || query.length < 2) return [];
  const q = query.toLowerCase();
  return ALL_LOCATIONS.filter((location) => location.toLowerCase().includes(q)).slice(
    0,
    limit,
  );
}
